import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import queryString from 'querystring';
import '@prisjakt/ui-base/main.scss';
import { bemGenerator, noop } from '@prisjakt/ui-base';
import { ViewportTracker } from '@prisjakt/ui-viewport';
import '@prisjakt/ui-base/styles/normalize.css';
import ExperimentEditor from '@prisjakt/ui-experiment/src/editor';
import { AppContext } from '@prisjakt/ui-app-context';
import Spinner from '@prisjakt/ui-spinner';

import MainHeader from './areas/header';
import Legacy from './areas/legacy';
import Footer from './areas/footer';
import CookieBanner from './components/cookie-banner';
import ErrorMessage from './components/error-message';
import ErrorBoundary from './components/error-boundary';
import ToastContainer from './components/toast-container';
import Sidebar from './areas/sidebar';
import Viewport from './components/viewport';
import FetchContext from './components/fetch-context';
import MobileFontSizeExperiment from './components/mobile-font-size-experiment';
import StaticIntercom from './components/static-intercom';
import NewsletterSignupModal, {
  NEWSLETTER_PARAM,
} from './components/newsletter-signup-modal';

import Feedback from './areas/feedback';
import { usePrefetchAllLoadOrders } from './hooks/experiments';

import TransitionRouter from './routes';
import TocModal from './components/toc-modal';
import addPerformanceTiming from './client/performance-reporter';

import './components/toast-container/sagas/toast';

import routeConfig from './routes/config';

import './app.scss';
import * as S from './styles';

const styles = bemGenerator('app', ['side-ads', 'cookie-banner-top']);

const Error500 = () => <ErrorMessage status={500} />;
const Error500WithMessage = error => (
  <ErrorMessage message={error.message} stack={error.stack} status={500} />
);

/**
 * A number so that we can track the render after the
 * initial mounting. This is when experiments are loaded and viewportcontext is updated.
 * This is used to add user performance timings.
 */
let renders = 0;

const App = props => {
  const {
    layout,
    campaigns,
    me,
    user,
    isAuthenticated,
    renderToastContainer,
    renderRoutes,
  } = props;
  const prefetchAllLoadOrders = usePrefetchAllLoadOrders();

  const {
    market: { routes, code },
    renderMode,
  } = useContext(AppContext);

  renders += 1;
  if (renders === 2) {
    addPerformanceTiming('firstRerender');
  }

  const legacyMode = ['desktop', 'mobile', 'native'].includes(renderMode);

  const router = renderRoutes ? (
    <FetchContext.Consumer>
      {fetchContext => (
        <TransitionRouter
          loaderAfter={1500}
          prefetchAllLoadOrders={prefetchAllLoadOrders}
          fetchContext={fetchContext}
          campaigns={campaigns}
          routes={routes}
          personalizationClientId={
            user && user.personalization && user.personalization.clientId
          }
          isAuthenticated={isAuthenticated}
          routeConfig={routeConfig}
        />
      )}
    </FetchContext.Consumer>
  ) : (
    <Spinner size={32} isActive fadeIn={false} />
  );

  if (legacyMode) {
    return (
      <ErrorBoundary onError={Error500}>
        <ViewportTracker>
          <Legacy router={router} />
        </ViewportTracker>
      </ErrorBoundary>
    );
  }

  const searchQuery = queryString.decode(props.location.search.substr(1))
    .search;

  const isFrontPage = ['/', ''].includes(props.location.pathname);
  const openNewsletterSignup = Object.keys(
    queryString.parse(props.location.search.substr(1))
  ).includes(NEWSLETTER_PARAM);

  return (
    <ErrorBoundary onError={Error500}>
      <div
        className={styles.block.withModifier({
          [layout]: true,
        })}
      >
        <MobileFontSizeExperiment />

        {openNewsletterSignup && <NewsletterSignupModal />}

        <Viewport tablet mobile>
          <ExperimentEditor />
        </Viewport>

        <ViewportTracker>
          <MainHeader searchQuery={searchQuery} />
          {renderToastContainer && <ToastContainer />}
          <Viewport desktop tablet>
            <Feedback />
          </Viewport>
          <ErrorBoundary onError={noop}>
            <CookieBanner />
          </ErrorBoundary>
          <ErrorBoundary onError={noop}>
            <StaticIntercom />
          </ErrorBoundary>
          <ErrorBoundary onError={Error500WithMessage}>
            <S.Main
              data-test="AppMain"
              data-test-market-id={code}
              sidebarOpen={props.sidebarOpen}
            >
              <Sidebar isFrontPage={isFrontPage} />

              <S.Content sidebarOpen={props.sidebarOpen}>
                {router}
                <Footer />
              </S.Content>
            </S.Main>
          </ErrorBoundary>
          {me && me.updatedTerms.length > 0 && (
            <TocModal items={me.updatedTerms} />
          )}
        </ViewportTracker>
      </div>
    </ErrorBoundary>
  );
};

App.propTypes = {
  infoPages: PropTypes.arrayOf(PropTypes.number),
  campaigns: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  location: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  me: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  user: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  layout: PropTypes.string,
  isAuthenticated: PropTypes.bool.isRequired,
  renderToastContainer: PropTypes.bool,
};

App.defaultProps = {
  infoPages: [],
  layout: 'default',
  me: undefined,
  renderToastContainer: false,
};

export default withRouter(
  connect(state => ({
    ...state.globals,
    me: state.areas.account.me,
    user: state.areas.user,
    isAuthenticated: state.areas.account.isAuthenticated,
    renderToastContainer: state.toast.required,
    sidebarOpen: state.areas.sidebar.open,
  }))(memo(App))
);
